import {isUndefined} from 'lodash';
import {compile, match} from 'path-to-regexp';
import React, {FC, PropsWithChildren, useMemo} from 'react';

import {
  getHasSettingsNavigationV2,
  getInitialFeatureFlags,
} from '../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {createSelector} from '../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../helpers/appReduxHelpers';
import {SettingsRouteFindResult, settingsRoutes, settingsRoutesV2} from '../../settings/settingsRoutes';
import {getFindHasCompanyFeature} from '../../store/data/company/companySelectors';
import {LegacyFrameContext} from './legacyFrameContext';
import {legacySettingsRewriteRoutes} from './legacyRewriteRoutes';

/*
 * Selectors.
 */

export function makeFindNewLegacyRoute() {
  return createSelector(
    [getInitialFeatureFlags, getFindHasCompanyFeature, getHasSettingsNavigationV2],
    (initialFlags, hasCompanyFeature, hasSettingsNavigationV2) => (currentPath: string | undefined) => {
      if (isUndefined(currentPath)) {
        return undefined;
      }

      const routes = hasSettingsNavigationV2 ? settingsRoutesV2 : settingsRoutes;

      return routes.reduce<SettingsRouteFindResult | undefined>((findResult, route) => {
        if (findResult) {
          return findResult;
        }

        const matchResult = matchPathOnly(currentPath, route.path);
        if (!matchResult) {
          return undefined;
        }

        if (route.featureFlag && !initialFlags[route.featureFlag]) {
          return undefined;
        }

        const matchProps = {
          ...matchResult,
          path: route.path,
          url: currentPath,
        };

        if (route.validate && !route.validate(matchProps, hasCompanyFeature, initialFlags)) {
          return undefined;
        }

        return {
          route,
          match: matchProps,
        };
      }, undefined);
    },
  );
}

function makeFindRewriteLegacyRoute() {
  return createSelector([getInitialFeatureFlags], (initialFlags) => (currentPath: string | undefined) => {
    if (isUndefined(currentPath)) {
      return undefined;
    }

    for (const route of legacySettingsRewriteRoutes) {
      if (route.featureFlag && !initialFlags[route.featureFlag]) {
        continue;
      }

      const matchResult = matchPathOnly(currentPath, route.legacyPath);
      if (!matchResult) {
        continue;
      }

      const [newPath, newPathParams] = route.newPath.split('?');
      return compile(newPath)(matchResult.params) + (newPathParams ? `?${newPathParams}` : '');
    }
    return undefined;
  });
}

function matchPathOnly(pathAndQuery: string, route: string) {
  return match(route.split('?')[0])(pathAndQuery.split('?')[0]);
}

/*
 * Component.
 */

export const LegacyFrameProvider: FC<PropsWithChildren<unknown>> = (props) => {
  const findNewLegacyRoute = useAppSelector(makeFindNewLegacyRoute());
  const findRewriteLegacyRoute = useAppSelector(makeFindRewriteLegacyRoute());
  const contextValue = useMemo(
    () => ({
      findNewLegacyRoute,
      findRewriteLegacyRoute,
    }),
    [findNewLegacyRoute, findRewriteLegacyRoute],
  );

  return <LegacyFrameContext.Provider value={contextValue}>{props.children}</LegacyFrameContext.Provider>;
};
